import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

// Define user roles
export type UserRole = 'admin' | 'editor' | 'viewer' | string;

// Define permissions
export type Permission = 
  | 'create:guest'
  | 'edit:guest'
  | 'delete:guest'
  | 'view:guest'
  | 'create:nomination'
  | 'edit:nomination'
  | 'delete:nomination'
  | 'view:nomination'
  | 'create:application'
  | 'edit:application'
  | 'delete:application'
  | 'view:application'
  | 'create:target-guest'
  | 'edit:target-guest'
  | 'delete:target-guest'
  | 'view:target-guest'
  | 'canManageGuests' // Added for compatibility with ProtectedRoute
  | 'access:settings'; // Permission for accessing admin settings

// Define auth context type
export interface AuthContextType {
  user: any;
  userRole: UserRole;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: Permission) => boolean;
}

// Create auth context
const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: 'viewer',
  isLoading: true,
  isAuthenticated: false,
  hasPermission: () => false,
});

// Define role-based permissions
const rolePermissions: Record<string, Permission[]> = {
  'admin': [
    'create:guest', 'edit:guest', 'delete:guest', 'view:guest',
    'create:nomination', 'edit:nomination', 'delete:nomination', 'view:nomination',
    'create:application', 'edit:application', 'delete:application', 'view:application',
    'create:target-guest', 'edit:target-guest', 'delete:target-guest', 'view:target-guest',
    'canManageGuests',
    'access:settings' // Grant permission to admin
  ],
  'editor': [
    'create:guest', 'edit:guest', 'view:guest',
    'edit:nomination', 'view:nomination',
    'edit:application', 'view:application',
    'edit:target-guest', 'view:target-guest'
  ],
  'viewer': [
    'view:guest',
    'view:nomination',
    'view:application',
    'view:target-guest'
  ],
  'default': []
};

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, userId, getToken } = useAuth();
  const { user } = useUser();
  const [userRole, setUserRole] = useState<UserRole>('viewer');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    if (isLoaded && user && getToken) {
      const determineUserRole = async () => {
        setIsLoadingProfile(true);
        let determinedRole: UserRole = 'viewer'; // Default role

        try {
          // First, try to get role from database profile
          const token = await getToken();
          if (token) {
            console.log('[useAuthContext] Fetching user profile to determine role...');
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/users/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const data = await response.json() as { success: boolean; profile?: any };
              if (data.success && data.profile) {
                console.log('[useAuthContext] Database profile:', data.profile);
                // Check database admin status first
                if (data.profile.isAdmin === true) {
                  determinedRole = 'admin';
                  console.log('[useAuthContext] User is admin based on database profile');
                } else if (data.profile.accessLevel === 'admin') {
                  determinedRole = 'admin';
                  console.log('[useAuthContext] User is admin based on accessLevel');
                } else if (data.profile.accessLevel === 'editor') {
                  determinedRole = 'editor';
                } else {
                  determinedRole = 'viewer';
                }
              }
            }
          }
        } catch (error) {
          console.error('[useAuthContext] Error fetching profile for role determination:', error);
        }

        // Fallback to Clerk metadata if database lookup failed
        if (determinedRole === 'viewer') {
          // 1. Check Clerk's public metadata
          if (user.publicMetadata && typeof user.publicMetadata.userRole === 'string' && user.publicMetadata.userRole.trim().length > 0) {
            determinedRole = user.publicMetadata.userRole as UserRole;
            console.log('[useAuthContext] Using Clerk metadata role:', determinedRole);
          } 
          // 2. Check for isAdmin in Clerk metadata
          else if (user.publicMetadata && user.publicMetadata.isAdmin === true) {
            determinedRole = 'admin';
            console.log('[useAuthContext] User is admin based on Clerk metadata isAdmin');
          }
          // 3. Fallback to email-based logic if no role in metadata 
          else if (user.primaryEmailAddress?.emailAddress) {
            const email = user.primaryEmailAddress.emailAddress;
            if (email.endsWith('@admin.com')) {
              determinedRole = 'admin';
            } else if (email.endsWith('@editor.com')) {
              determinedRole = 'editor';
            }
            // If email doesn't match and no valid metadata role, it remains 'viewer' from initial default
          }
        }

        console.log('[useAuthContext] Final determined role:', determinedRole);
        setUserRole(determinedRole);
        setIsLoadingProfile(false);
      };

      determineUserRole();
    } else if (!isLoaded || !user) {
      setUserRole('viewer'); // Reset if not loaded or user signs out
      setIsLoadingProfile(false);
    }
  }, [isLoaded, user, getToken]);

  // Check if user has permission
  const hasPermission = (permission: Permission): boolean => {
    if (!userId) return false;
    
    const permissions = rolePermissions[userRole] || rolePermissions['default'];
    return permissions ? permissions.includes(permission) : false;
  };

  // Add role property to user for compatibility
  const userWithRole = user ? { ...user, role: userRole } : null;

  return (
    <AuthContext.Provider
      value={{
        user: userWithRole,
        userRole,
        isLoading: !isLoaded || isLoadingProfile,
        isAuthenticated: !!userId && isLoaded,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuthContext = () => useContext(AuthContext);
